import React, { useState } from "react";
import AccountLayout from "../../layouts/AccountLayout";
import { useTranslation } from "react-i18next";
import { Cell } from "react-table";
import ManufacturerTable from "../dashboard/ManufacturerTable";
import { formatDateAndTime } from "../../utils";
import { Manufacturer } from "../../interfaces";
import { useRequest } from "ahooks";
import { createManufacturerApi, getManufacturers } from "../../api";
import { LoadingOutlined } from "@ant-design/icons";
import Modal from "../modal/Modal";
import { toast } from "react-toastify";
import { Button, Loader, Text, TextInput } from "@mantine/core";

const Manufacturers: React.FC = () => {
  const { t } = useTranslation();

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const { data, loading, refresh } = useRequest<Manufacturer[]>(() =>
    getManufacturers(),
  );

  const columns = [
    {
      accessor: "id",
      Header: t("manufacturerId"),
      Footer: t("manufacturerId"),
    },
    {
      accessor: "email",
      Header: t("manufacturerEmail"),
      Footer: t("manufacturerEmail"),
    },
    {
      accessor: "name",
      Header: t("manufacturerName"),
      Footer: t("manufacturerName"),
    },
    {
      accessor: "createdAt",
      Header: t("createdDate"),
      Footer: t("createdDate"),
      Cell: (cell: Cell) => formatDateAndTime(cell.value),
    },
  ];

  const onModalOpen = () => {
    const form = document.getElementById(
      "manufacturerCreateForm",
    ) as HTMLFormElement;
    form?.reset();
    setModalVisible(true);
  };

  const onModalClose = () => {
    setModalVisible(false);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    const name = e.target["manufacturerName"].value;
    const email = e.target["manufacturerEmail"].value;

    createAction.run({ name, email });
  };

  const createAction = useRequest(
    (values: { email: string; name: string }) => createManufacturerApi(values),
    {
      manual: true,
      onSuccess: (res: any) => {
        if (res && typeof res === "boolean") {
          toast.success(t("manufacturerCreateSuccess"));
          onModalClose();
          refresh();
          return;
        }

        if (res?.message) {
          if (
            /An account with the given email already exists/.test(res?.message)
          ) {
            toast.error(t("emailExists"));
          } else {
            toast.error(t("manufacturerCreateFailed"));
          }
        }
      },
    },
  );

  return (
    <AccountLayout account>
      <Button onClick={onModalOpen} mb={30}>
        {t("createManufacturer")}
      </Button>

      {!loading && data ? (
        <>
          {data?.length === 0 ? (
            <div className="row row-center mt-30">
              <p>{t("noManufacturers")}</p>
            </div>
          ) : (
            <ManufacturerTable columns={columns} data={data} />
          )}
        </>
      ) : (
        <div className="row row-center mt-30">
          <Loader />
        </div>
      )}
      <Modal compact center visible={modalVisible} onClose={onModalClose}>
        <div className="text-center mb-20">
          <Text size="xl" weight={500}>
            {t("createManufacturer")}
          </Text>
        </div>
        <form
          id="manufacturerCreateForm"
          className="form"
          onSubmit={handleSubmit}
        >
          <div className="form__row">
            <Text>{t("manufacturerName")}</Text>
            <TextInput
              name="manufacturerName"
              type="text"
              required
              placeholder={t("manufacturerName")}
            />
          </div>
          <div className="form__row">
            <Text>{t("manufacturerEmail")}</Text>
            <TextInput
              name="manufacturerEmail"
              type="email"
              required
              placeholder={t("manufacturerEmail")}
            />
          </div>
          <div className="form__row">
            <Button
              mt={10}
              size="lg"
              loading={createAction.loading}
              disabled={createAction.loading}
              type="submit"
            >
              {createAction.loading ? (
                <LoadingOutlined />
              ) : (
                t("createManufacturer")
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </AccountLayout>
  );
};

export default Manufacturers;
